/**
 * Booking intake log — the electronic Auftrag-Eingangs-Buch.
 *
 * § 49 Abs. 4 Satz 4 PBefG: "Den Eingang des Beförderungsauftrages am
 * Betriebssitz oder in der Wohnung hat der Mietwagenunternehmer buchmäßig oder
 * elektronisch (auch mittels appbasierten Systems) zu erfassen und die
 * Aufzeichnung ein Jahr aufzubewahren."
 *
 * This is the "wann die Fahrt bestellt wurde" field from the source concept,
 * and it is a legal record in its own right rather than a comment on the trip
 * — which is why it lives in its own table and can be captured before the trip
 * exists. The Finanzverwaltung has expressly rejected an Excel sheet for this
 * purpose.
 *
 * The form follows the columns of the printed HUSS 19006 order book
 * (`01_Input/referenz/README.md`), in the same order and with the same
 * mandatory markings, so an operator moving off paper recognises every field.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Callout,
  Card,
  EmptyState,
  Field,
  SectionHeading,
  Select,
  TextArea,
  TextInput,
} from '@/components/ui';
import { formatDateTime, nowIso, todayIso } from '@/domain/dates';
import type { Order, OrderAcceptancePlace, OrderChannel } from '@/domain/types';
import { orderRepo } from '@/db/repositories';
import { useSession } from '@/session/SessionContext';
import { useT } from '@/i18n';
import {
  ACCEPTANCE_SHORT,
  createOrder,
  findOrderSequenceGaps,
  nextOrderSequence,
  reviewOrder,
} from './orderService';

export function OrderListScreen() {
  const session = useSession();
  const t = useT();
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [gaps, setGaps] = useState<number[]>([]);
  const [showForm, setShowForm] = useState(false);

  async function reload() {
    const all = await orderRepo.all(session.dataKey);
    setOrders(all.sort((a, b) => b.receivedAt.localeCompare(a.receivedAt)));
    setGaps(await findOrderSequenceGaps(todayIso().slice(0, 4)));
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.dataKey]);

  return (
    <div className="space-y-5">
      <SectionHeading
        title={t.orders.title}
        description={t.orders.description}
        action={
          <button type="button" className="btn-primary" onClick={() => setShowForm((v) => !v)}>
            {showForm ? t.common.cancel : t.orders.new}
          </button>
        }
      />

      <Callout tone="info">{t.orders.intro}</Callout>

      {gaps.length > 0 && (
        <Callout tone="danger" title={t.orders.gapTitle}>
          {t.orders.gapBody(gaps.join(', '))}
        </Callout>
      )}

      {showForm && (
        <OrderForm
          onSaved={async () => {
            setShowForm(false);
            await reload();
          }}
        />
      )}

      {!orders ? (
        <p className="text-muted text-sm">{t.common.loading}</p>
      ) : orders.length === 0 ? (
        <EmptyState title={t.orders.empty} />
      ) : (
        <ul className="space-y-2">
          {orders.map((order) => (
            <OrderRow key={order.id} order={order} />
          ))}
        </ul>
      )}
    </div>
  );
}

function OrderRow({ order }: { order: Order }) {
  const session = useSession();
  const t = useT();
  const vehicle = session.vehicles.find((v) => v.id === order.vehicleId);
  // Show the most serious finding rather than all of them: the row is a list
  // entry, not a form, and one visible gap is enough to make someone open it.
  const [finding] = reviewOrder(order).sort((a, b) =>
    a.severity === b.severity ? 0 : a.severity === 'pflicht' ? -1 : 1,
  );

  return (
    <li className="card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-heading text-sm font-medium">
            <span className="text-muted tabular-nums">
              {t.orders.number(order.sequenceNumber)}
            </span>{' '}
            {order.ordererName || t.orders.noName}
          </p>
          <p className="text-muted mt-0.5 text-xs">
            {t.orders.received(formatDateTime(order.receivedAt))} ·{' '}
            {t.labels.channel[order.channel]}
            {order.acceptancePlace &&
              t.orders.acceptedAs(
                ACCEPTANCE_SHORT[order.acceptancePlace],
                t.labels.acceptance[order.acceptancePlace],
              )}
          </p>
          <p className="text-muted mt-0.5 text-xs">
            {order.pickupLocation || t.common.dash}
            {order.destination ? ` → ${order.destination}` : ''}
            {order.requestedPickupAt &&
              t.orders.requested(formatDateTime(order.requestedPickupAt))}
            {vehicle && ` · ${vehicle.licensePlate}`}
          </p>
          {order.notes && <p className="text-muted mt-1 text-xs">{order.notes}</p>}
          {finding && (
            <p
              className={`mt-1 text-xs ${
                finding.severity === 'pflicht' ? 'text-amber-700' : 'text-muted'
              }`}
            >
              {finding.message(t)}
            </p>
          )}
        </div>
        {order.tripId ? (
          <Link to={`/fahrten/${order.tripId}`}>
            <Badge tone="success">{t.orders.linkedTrip}</Badge>
          </Link>
        ) : (
          <Link to={`/fahrten/neu?auftrag=${order.id}`} className="btn-secondary text-xs">
            {t.orders.logTrip}
          </Link>
        )}
      </div>
    </li>
  );
}

function OrderForm({ onSaved }: { onSaved: () => Promise<void> }) {
  const session = useSession();
  const t = useT();
  const [receivedAt, setReceivedAt] = useState(() => toLocalInput(nowIso()));
  const [channel, setChannel] = useState<OrderChannel>('telefon');
  const [ordererName, setOrdererName] = useState('');
  const [ordererAddress, setOrdererAddress] = useState('');
  const [ordererPhone, setOrdererPhone] = useState('');
  const [ordererEmail, setOrdererEmail] = useState('');
  const [pickupLocation, setPickupLocation] = useState('');
  const [destination, setDestination] = useState('');
  const [requestedPickup, setRequestedPickup] = useState('');
  const [accepted, setAccepted] = useState(true);
  const [acceptancePlace, setAcceptancePlace] = useState<OrderAcceptancePlace>('betriebssitz');
  const [acceptedAt, setAcceptedAt] = useState(() => toLocalInput(nowIso()));
  const [vehicleId, setVehicleId] = useState(session.app.defaultVehicleId ?? '');
  const [notes, setNotes] = useState('');
  const [nextNumber, setNextNumber] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void nextOrderSequence(new Date(receivedAt).toISOString()).then(setNextNumber);
  }, [receivedAt]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!pickupLocation.trim()) {
      setError(t.orders.pickupRequired);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createOrder(session.audit, {
        receivedAt: new Date(receivedAt).toISOString(),
        channel,
        ordererName: ordererName.trim() || undefined,
        ordererAddress: ordererAddress.trim() || undefined,
        ordererPhone: ordererPhone.trim() || undefined,
        ordererEmail: ordererEmail.trim() || undefined,
        pickupLocation: pickupLocation.trim(),
        destination: destination.trim() || undefined,
        requestedPickupAt: requestedPickup ? new Date(requestedPickup).toISOString() : undefined,
        acceptancePlace: accepted ? acceptancePlace : undefined,
        acceptedAt: accepted ? new Date(acceptedAt).toISOString() : undefined,
        vehicleId: accepted ? vehicleId || undefined : undefined,
        driverId: accepted ? session.account.id : undefined,
        notes: notes.trim() || undefined,
      });
      await onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.common.saveFailed);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title={t.orders.formTitle(nextNumber)}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <fieldset className="space-y-4">
          <legend className="text-muted mb-2 text-xs font-semibold tracking-wide uppercase">
            {t.orders.groupIntake}
          </legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t.orders.receivedAt} required hint={t.orders.receivedAtHint}>
              <TextInput
                type="datetime-local"
                value={receivedAt}
                onChange={(event) => setReceivedAt(event.target.value)}
              />
            </Field>
            <Field label={t.orders.channel} required>
              <Select
                value={channel}
                onChange={(event) => setChannel(event.target.value as OrderChannel)}
              >
                {Object.entries(t.labels.channel).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="text-muted mb-2 text-xs font-semibold tracking-wide uppercase">
            {t.orders.groupOrderer}
          </legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t.orders.ordererName}>
              <TextInput
                value={ordererName}
                onChange={(event) => setOrdererName(event.target.value)}
              />
            </Field>
            <Field label={t.orders.ordererAddress}>
              <TextInput
                value={ordererAddress}
                onChange={(event) => setOrdererAddress(event.target.value)}
              />
            </Field>
            <Field label={t.orders.ordererPhone}>
              <TextInput
                type="tel"
                value={ordererPhone}
                onChange={(event) => setOrdererPhone(event.target.value)}
              />
            </Field>
            <Field label={t.orders.ordererEmail}>
              <TextInput
                type="email"
                value={ordererEmail}
                onChange={(event) => setOrdererEmail(event.target.value)}
              />
            </Field>
          </div>
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="text-muted mb-2 text-xs font-semibold tracking-wide uppercase">
            {t.orders.groupRide}
          </legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t.orders.pickup} required hint={t.orders.pickupHint}>
              <TextInput
                value={pickupLocation}
                onChange={(event) => setPickupLocation(event.target.value)}
              />
            </Field>
            <Field label={t.orders.destination} hint={t.orders.destinationHint}>
              <TextInput
                value={destination}
                onChange={(event) => setDestination(event.target.value)}
              />
            </Field>
            <Field label={t.orders.requestedPickup}>
              <TextInput
                type="datetime-local"
                value={requestedPickup}
                onChange={(event) => setRequestedPickup(event.target.value)}
              />
            </Field>
          </div>
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="text-muted mb-2 text-xs font-semibold tracking-wide uppercase">
            {t.orders.groupAcceptance}
          </legend>

          <label className="text-body flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(event) => setAccepted(event.target.checked)}
              className="accent-primary h-4 w-4"
            />
            {t.orders.acceptedToggle}
          </label>

          {accepted ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={t.orders.acceptancePlace}
                required
                hint={t.orders.acceptancePlaceHint}
              >
                <Select
                  value={acceptancePlace}
                  onChange={(event) =>
                    setAcceptancePlace(event.target.value as OrderAcceptancePlace)
                  }
                >
                  {Object.entries(t.labels.acceptance).map(([value, label]) => (
                    <option key={value} value={value}>
                      {ACCEPTANCE_SHORT[value as OrderAcceptancePlace]} — {label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t.orders.acceptedAt} required>
                <TextInput
                  type="datetime-local"
                  value={acceptedAt}
                  onChange={(event) => setAcceptedAt(event.target.value)}
                />
              </Field>
              <Field label={t.orders.vehicle} required>
                <Select value={vehicleId} onChange={(event) => setVehicleId(event.target.value)}>
                  <option value="">{t.orders.vehicleNone}</option>
                  {session.vehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.vehicleCode} · {vehicle.licensePlate}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t.orders.driver}>
                <TextInput value={session.account.displayName} readOnly disabled />
              </Field>
            </div>
          ) : (
            <p className="text-muted text-xs">{t.orders.acceptanceLater}</p>
          )}
        </fieldset>

        <Field label={t.orders.notes}>
          <TextArea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>

        {error && <Callout tone="danger">{error}</Callout>}

        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? t.common.saving : t.orders.new}
        </button>
      </form>
    </Card>
  );
}

/** `datetime-local` wants local time without a zone suffix. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}
